import React from "react";
import { FeatherAlertCircle } from "@subframe/core";
import { Badge } from "../../ui";
import { VendorPaymentRefundCategoryDetails } from "../../services/api";

interface CustomerVendorPaymentRefundViewContentProps {
  details: VendorPaymentRefundCategoryDetails | null;
  isLoading: boolean;
}

const formatDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  return isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
};

const InfoCard: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col rounded border border-neutral-border bg-white px-4 py-3">
    <span className="text-caption text-subtext-color">{label}</span>
    <span className="text-body-bold text-default-font">{value || "—"}</span>
  </div>
);

const CustomerVendorPaymentRefundViewContent: React.FC<
  CustomerVendorPaymentRefundViewContentProps
> = ({ details, isLoading }) => {
  const hasDetails = Boolean(details);

  if (!details) {
    return (
      <div className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed border-neutral-border bg-neutral-50 px-6 py-10 text-center text-body text-subtext-color">
        <FeatherAlertCircle className="text-neutral-400" />
        <span>Customer payment refund details are not available.</span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Vendor Payment Refund Details
          </span>
          {details?.vendor_name && (
            <Badge variant="neutral">Vendor: {details.vendor_name}</Badge>
          )}
          {details?.to_account_name && (
            <Badge className="border-neutral-700 bg-neutral-700 text-white [&>span]:text-white">
              To Account: {details.to_account_name}
            </Badge>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12 text-body text-subtext-color">
          Loading transaction details...
        </div>
      ) : !hasDetails ? (
        <div className="flex w-full flex-col items-center gap-2 rounded border border-dashed border-neutral-border px-4 py-6 text-center text-body text-subtext-color">
          <FeatherAlertCircle className="text-neutral-400" />
          <span>No vendor payment refund details available.</span>
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <InfoCard label="Date" value={formatDate(details?.date)} />
            <InfoCard
              label="Received Via"
              value={details?.payment_mode || "—"}
            />
            <InfoCard
              label="Reference Number"
              value={details?.reference_number || "—"}
            />
          </div>

          {!details.payment ? (
            <div className="flex w-full flex-col items-center gap-2 rounded border border-dashed border-neutral-border px-4 py-6 text-center text-body text-subtext-color">
              <FeatherAlertCircle className="text-neutral-400" />
              <span>No vendor payment was linked to this refund.</span>
            </div>
          ) : (
            <div className="flex w-full flex-col gap-3 rounded border border-solid border-neutral-border bg-white px-6 py-4">
              <div className="flex items-center justify-between border-b border-neutral-border pb-3">
                <span className="text-body-bold text-subtext-color">
                  Payment Details
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <span className="text-caption text-subtext-color">
                    Payment #
                  </span>
                  <span className="text-body-bold text-default-font">
                    {details.payment.payment_number ||
                      details.payment.payment_id}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-caption text-subtext-color">
                    Amount Refunded
                  </span>
                  <span className="text-heading-4 font-heading-4 text-success-600">
                    ₹ {details.payment.amount_refunded.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CustomerVendorPaymentRefundViewContent;
